import type { MetaFunction } from "react-router";
import { Link } from "react-router";
import { SITE_ORIGIN } from "../brand";
import {
  APP_STORE_URL,
  marketingLinks,
  MarketingShell,
  socialImageMeta,
} from "../components/marketing/layout";
import { ArrowRight } from "../components/marketing/icons";

const PAGE_URL = `${SITE_ORIGIN}/how-to-build-an-fda-nutrition-facts-label`;

export const links = marketingLinks;

export const meta: MetaFunction = () => [
  { title: "Build an FDA-Style Nutrition Facts Label — Guide" },
  {
    name: "description",
    content:
      "Learn how to format an FDA-style Nutrition Facts panel using serving size, nutrient values, rounding rules, Daily Values, and required layout elements.",
  },
  { name: "robots", content: "index,follow" },
  { tagName: "link", rel: "canonical", href: PAGE_URL },
  { property: "og:url", content: PAGE_URL },
  { property: "og:type", content: "article" },
  { property: "og:title", content: "How to Build an FDA Nutrition Facts Label" },
  {
    property: "og:description",
    content:
      "Serving size, nutrient data, the exact rounding tables, %DV math, and layout — the whole job, step by step.",
  },
  ...socialImageMeta(),
  {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "How to Build an FDA Nutrition Facts Label",
      description:
        "A practical walkthrough of 21 CFR 101.9 for food and supplement brands: serving size, nutrient values, rounding increments, % Daily Values, layout, and common mistakes.",
      datePublished: "2026-08-09",
      dateModified: "2026-08-09",
      author: { "@type": "Organization", name: "Legitimate LLC" },
      publisher: { "@type": "Organization", name: "Legitimate LLC" },
      mainEntityOfPage: PAGE_URL,
    },
  },
];

export default function Guide() {
  return (
    <MarketingShell>
      <section className="nm-section">
        <div className="nm-wrap">
          <article className="nm-article">
            <div className="nm-article__meta">
              <span>Guide</span>
              <span aria-hidden>·</span>
              <span>21 CFR 101.9</span>
              <span aria-hidden>·</span>
              <span>Updated 2026-08-09</span>
              <span aria-hidden>·</span>
              <span>~12 min read</span>
            </div>
            <h1>How to build an FDA Nutrition Facts label</h1>
            <p className="nm-article__standfirst">
              The Nutrition Facts panel looks simple because fifty years of
              regulation sanded it down to one job: be readable at arm&rsquo;s
              length in a grocery aisle. Building a correct one is five steps —
              serving size, nutrient values, rounding, % Daily Values, layout —
              and each has rules that are exact, published, and unforgiving. This
              guide walks all five, with the actual tables, then shows where
              software earns its keep.
            </p>

            <div className="nm-prose">
              <p>
                Everything below comes from <strong>21 CFR 101.9</strong> (the
                Nutrition Facts rules) and <strong>21 CFR 101.12</strong> (the
                reference-amount tables). It applies to conventional foods sold
                in the US; dietary supplements use the sibling format in{" "}
                <strong>21 CFR 101.36</strong>, covered at the end. One thing up
                front, because it matters more than any table:{" "}
                <strong>
                  a correctly formatted panel is not the same as a compliant
                  product.
                </strong>{" "}
                The values you print are your responsibility, and a qualified
                labeling expert should verify the final artwork before it ships.
              </p>

              <h2>Step 1 — Fix the serving size before anything else</h2>
              <p>
                Every number on the panel is &ldquo;per serving,&rdquo; so the
                serving size is the load-bearing decision. You don&rsquo;t choose
                it freely: the FDA publishes <strong>Reference Amounts
                Customarily Consumed</strong> (RACCs) per food category in 21 CFR
                101.12 — 55 g for granola, 240 ml for most beverages, 30 g for
                chips, and so on.
              </p>
              <ul>
                <li>
                  <strong>Find your category&rsquo;s RACC</strong> in the 101.12
                  tables. Your serving is the household measure (cups, pieces,
                  tablespoons) that gets closest to the RACC, followed by the
                  metric weight in parentheses: <em>2/3 cup (55g)</em>.
                </li>
                <li>
                  <strong>Declare servings per container</strong> — the
                  container&rsquo;s net weight divided by the serving weight,
                  rounded per the rule (nearest 0.5 between 2 and 5, nearest
                  whole number above 5).
                </li>
                <li>
                  <strong>Check the dual-column trigger.</strong> If the whole
                  package holds between 200% and 300% of the RACC — a pint of
                  ice cream is the classic case — you must show both per-serving
                  and per-package columns. Single-serving containers that are
                  ≤200% of the RACC are labeled as one serving.
                </li>
              </ul>
              <p>
                Get this wrong and every other number on the panel is wrong with
                it. Get it right and the rest is arithmetic.
              </p>

              <h2>Step 2 — Get nutrient values you can defend</h2>
              <p>
                There are three legitimate sources of per-serving nutrient data,
                in descending order of defensibility:
              </p>
              <ol>
                <li>
                  <strong>Laboratory analysis.</strong> An AOAC-method nutrient
                  analysis of your finished product. This is the gold standard
                  and the only source that accounts for your actual recipe,
                  process losses, and lot-to-lot variance. Expect a few hundred
                  dollars per product.
                </li>
                <li>
                  <strong>Database calculation.</strong> Build the recipe from
                  reference data — the USDA&rsquo;s FoodData Central is the
                  canonical US source — scaled by ingredient weights and yield.
                  Respectable for simple products with minimal processing; shaky
                  wherever frying, brining, or fermentation changes the profile.
                </li>
                <li>
                  <strong>Supplier data.</strong> For a private-label product,
                  the manufacturer&rsquo;s spec sheet. Verify it&rsquo;s for your
                  formulation, not the house recipe.
                </li>
              </ol>
              <div className="nm-callout">
                <p>
                  <strong>Where the app fits:</strong> EasyNutriFact&rsquo;s
                  editor has a built-in USDA FoodData Central lookup that
                  prefills per-serving values from your ingredients. It&rsquo;s a
                  genuine head start for option 2 — and it is deliberately labeled
                  as a starting point, not an analysis. The numbers you print are
                  still yours to stand behind.
                </p>
              </div>

              <h2>Step 3 — Round every value by the FDA&rsquo;s increments</h2>
              <p>
                You never print raw analytical values. 21 CFR 101.9(c) prescribes
                a display increment per nutrient, with &ldquo;zero&rdquo; and
                &ldquo;less than&rdquo; thresholds — and midpoints round half up,
                not banker&rsquo;s rounding. These are the increments for the
                mandatory nutrients:
              </p>
              <div className="nm-tablewrap">
                <table className="nm-table">
                  <thead>
                    <tr>
                      <th scope="col">Nutrient</th>
                      <th scope="col">Zero below</th>
                      <th scope="col">Increment</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <th scope="row">Calories</th>
                      <td>&lt; 5 cal</td>
                      <td>Nearest 5 up to 50 cal; nearest 10 above 50</td>
                    </tr>
                    <tr>
                      <th scope="row">Total / saturated / trans fat</th>
                      <td>&lt; 0.5 g</td>
                      <td>Nearest 0.5 g below 5 g; nearest 1 g at 5 g and up</td>
                    </tr>
                    <tr>
                      <th scope="row">Cholesterol</th>
                      <td>&lt; 2 mg</td>
                      <td>&ldquo;Less than 5 mg&rdquo; from 2–5 mg; nearest 5 mg above</td>
                    </tr>
                    <tr>
                      <th scope="row">Sodium</th>
                      <td>&lt; 5 mg</td>
                      <td>Nearest 5 mg up to 140 mg; nearest 10 mg above</td>
                    </tr>
                    <tr>
                      <th scope="row">Carbohydrate, fiber, sugars, protein</th>
                      <td>&lt; 0.5 g</td>
                      <td>&ldquo;Less than 1 g&rdquo; from 0.5–1 g; nearest 1 g above</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p>
                Two things here catch hand-built labels constantly. First, the
                thresholds: 0.4 g of fat rounds to <em>0 g</em>, but 0.6 g rounds
                to <em>0.5 g</em>, not 1 g. Second, the rounding is for{" "}
                <strong>display only</strong> — the next step uses the unrounded
                numbers.
              </p>

              <h2>Step 4 — Compute % Daily Values from the unrounded values</h2>
              <p>
                The %DV column divides the nutrient amount by its Daily Value —
                the 2016-rule reference amounts — using the{" "}
                <strong>unrounded</strong> analytical value, then rounds the
                percentage itself. Macros with a Daily Value display to the
                nearest 1%. The four micronutrients use a coarser grid and a
                reporting threshold:
              </p>
              <ul>
                <li>Below 2% DV: the value may be omitted (or declared with a footnote).</li>
                <li>2–10%: nearest 2%.</li>
                <li>10–50%: nearest 5%.</li>
                <li>Above 50%: nearest 10%.</li>
              </ul>
              <p>The Daily Values for adults and children 4+ (2,000-calorie basis):</p>
              <div className="nm-tablewrap">
                <table className="nm-table">
                  <thead>
                    <tr>
                      <th scope="col">Nutrient</th>
                      <th scope="col">Daily Value</th>
                      <th scope="col">Nutrient</th>
                      <th scope="col">Daily Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <th scope="row">Total fat</th>
                      <td>78 g</td>
                      <th scope="row">Added sugars</th>
                      <td>50 g</td>
                    </tr>
                    <tr>
                      <th scope="row">Saturated fat</th>
                      <td>20 g</td>
                      <th scope="row">Protein</th>
                      <td>50 g</td>
                    </tr>
                    <tr>
                      <th scope="row">Cholesterol</th>
                      <td>300 mg</td>
                      <th scope="row">Vitamin D</th>
                      <td>20 mcg</td>
                    </tr>
                    <tr>
                      <th scope="row">Sodium</th>
                      <td>2,300 mg</td>
                      <th scope="row">Calcium</th>
                      <td>1,300 mg</td>
                    </tr>
                    <tr>
                      <th scope="row">Total carbohydrate</th>
                      <td>275 g</td>
                      <th scope="row">Iron</th>
                      <td>18 mg</td>
                    </tr>
                    <tr>
                      <th scope="row">Dietary fiber</th>
                      <td>28 g</td>
                      <th scope="row">Potassium</th>
                      <td>4,700 mg</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p>
                No Daily Value is established for trans fat or total sugars —
                those rows simply carry no percentage. Potassium&rsquo;s 4,700 mg
                is the current US value; don&rsquo;t copy the older 3,500 mg from
                pre-2016 labels or the 3,400 mg Canada is moving to.
              </p>
              <div className="nm-callout">
                <p>
                  <strong>Worked example, from the FDA&rsquo;s own granola
                  panel:</strong> 4.75 g of saturated fat <em>displays</em> as
                  4.5 g (nearest 0.5), but its %DV is 4.75 ÷ 20 = 23.75% →{" "}
                  <strong>24%</strong>, not the 23% you&rsquo;d get from the
                  rounded 4.5 g. The same rule is why the sample&rsquo;s 8 mg of
                  iron prints as 45% DV: the underlying 8.1 mg ÷ 18 mg = 45%.
                  Compute from unrounded values — always.
                </p>
              </div>

              <h2>Step 5 — Lay out the panel exactly</h2>
              <p>
                The format itself is regulated: black type on a white or neutral
                background, a hairline box, the <em>Nutrition Facts</em> title in
                the heaviest weight on the label, thick horizontal bars separating
                the title/calories block from the nutrients, hairlines between
                rows, and the specific nutrient order:{" "}
                <strong>
                  calories → total fat → saturated fat → trans fat → cholesterol
                  → sodium → total carbohydrate → dietary fiber → total sugars →
                  added sugars → protein → vitamin D → calcium → iron → potassium
                </strong>
                . Sub-nutrients indent under their parent; &ldquo;Includes X g
                Added Sugars&rdquo; double-indents under total sugars. The
                footnote — <em>* The % Daily Value (DV) tells you how much a
                nutrient in a serving of food contributes to a daily diet. 2,000
                calories a day is used for general nutrition advice.</em> — is
                mandatory text.
              </p>
              <p>
                Layout is where hand-built labels visibly drift: bars the wrong
                weight, a missing indent, the wrong footnote copy. It&rsquo;s
                also the easiest part for software to get right every time —
                which is exactly the argument for not doing steps 3–5 by hand.
              </p>

              <h2>The mistakes that actually get labels rejected</h2>
              <ul>
                <li>
                  <strong>Rounding %DV from the rounded amount</strong> instead of
                  the analytical value (the 24%-not-23% case above).
                </li>
                <li>
                  <strong>Stale Daily Values</strong> — sodium 2,400 mg or
                  potassium 3,500 mg are pre-2016 numbers; the current US values
                  are in the table above.
                </li>
                <li>
                  <strong>Invented serving sizes</strong> that dodge the RACC to
                  make calories look smaller. This is the first thing a reviewer
                  checks, and it poisons every other row.
                </li>
                <li>
                  <strong>&ldquo;Less than&rdquo; thresholds ignored</strong> —
                  printing &ldquo;0.7 g&rdquo; of fiber when the rule says
                  &ldquo;less than 1 g.&rdquo;
                </li>
                <li>
                  <strong>Treating the panel as the whole label.</strong> The
                  allergen &ldquo;Contains&rdquo; statement (FALCPA), the
                  ingredient list in descending weight order, and any nutrient
                  content claims (&ldquo;low sodium,&rdquo; &ldquo;high
                  fiber&rdquo;) are separate requirements with their own rules.
                  The panel doesn&rsquo;t cover them, and neither does this
                  guide.
                </li>
              </ul>

              <h2>If you sell supplements: 21 CFR 101.36</h2>
              <p>
                Supplements swap the format, not the logic. A Supplement Facts
                panel declares servings as a dosage form (&ldquo;2
                capsules&rdquo;), lists dietary ingredients with their amounts
                and %DV where established, uses a &dagger; footnote (&ldquo;Daily
                Value not established&rdquo;) for ingredients without one, and
                lists proprietary blends by total blend weight with ingredients
                in descending order. The rounding philosophy — declared
                increments, unrounded inputs — carries over.
              </p>

              <h2>Where software earns its keep</h2>
              <p>
                Steps 3 through 5 are deterministic: fixed increments, fixed
                Daily Values, a fixed layout. That is precisely the part of the
                job a computer should do — and it&rsquo;s what EasyNutriFact
                does inside your Shopify admin. Type or look up your values
                (steps 1–2 stay human), and the app applies every rounding rule
                and %DV grid above, renders the regulation layout live, saves the
                panel to the product, and exports a 300 DPI print-ready PNG.
                It&rsquo;s free.
              </p>
              <div className="nm-callout nm-callout--warn">
                <p>
                  <strong>The honest limit, again:</strong> no app can verify
                  that your numbers describe your product. EasyNutriFact
                  formats and rounds the values you provide — it doesn&rsquo;t
                  test recipes or guarantee regulatory compliance. Have a
                  qualified labeling expert verify the final label before
                  printing.
                </p>
              </div>
              <p style={{ marginTop: "2.2rem" }}>
                <a className="nm-btn" href={APP_STORE_URL}>
                  Build your label free
                  <ArrowRight />
                </a>{" "}
                <Link className="nm-btn nm-btn--ghost" to="/demo">
                  Try the live demo
                </Link>
              </p>
            </div>
          </article>
        </div>
      </section>
    </MarketingShell>
  );
}
